import { useCallback, useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useGetInvitesQuery } from '../../store/auth/auth'
import { useGetUsersQuery } from '../../store/user/user'
import { type InviteChipKey, type MemberChipKey, type TeamChipKey, isInviteChip } from './teamChips'
import {
  REQUESTED_SEAT_TYPES,
  type TeamQueryFilters,
  inviteCountFilter,
  inviteFilter,
  memberCountArgs,
  memberListArgs,
  requestedSeatTypeCountFilter,
} from './teamQueries'
import { type TeamRow, toInviteRow, toMemberRow } from './teamRows'

const COUNT_PAGE = { page: 0, size: 1 }

export interface UseTeamListArgs extends TeamQueryFilters {
  chip: TeamChipKey
  page: number
  perPage: number
  hasInviteScope: boolean
}

export interface UseTeamListResult {
  rows: TeamRow[]
  totalRows: number
  isFetching: boolean
  counts: Partial<Record<TeamChipKey, number>>
  requestedSeats: { advisor?: number; assistant?: number }
  requestedSeatsError: boolean
  refetch: () => void
}

export const useTeamList = ({ chip, page, perPage, hasInviteScope, namePattern, roleGroupNames }: UseTeamListArgs): UseTeamListResult => {
  const accountId = useAppSelector(selectAccountId)
  const showsInvites = isInviteChip(chip)
  const showsGroups = chip === 'requested'
  const filters = useMemo<TeamQueryFilters>(() => ({ namePattern, roleGroupNames }), [namePattern, roleGroupNames])

  const memberChip: MemberChipKey = chip === 'deactivated' ? 'deactivated' : 'active'
  const inviteChip: InviteChipKey = showsInvites ? chip : 'pending'

  const {
    data: memberPage,
    isFetching: membersFetching,
    refetch: refetchMembers,
  } = useGetUsersQuery(memberListArgs(accountId, memberChip, { page, perPage }, filters), {
    skip: showsInvites,
    refetchOnMountOrArgChange: true,
  })

  const { data: invitePage, isFetching: invitesFetching } = useGetInvitesQuery(
    { accountId, pageRequest: { page: page - 1, size: perPage }, filter: inviteFilter(inviteChip, filters) },
    { skip: !showsInvites || showsGroups, refetchOnMountOrArgChange: true },
  )

  const { data: activeCount, refetch: refetchActiveCount } = useGetUsersQuery(memberCountArgs(accountId, 'active'))

  const inviteCountArgs = (countChip: InviteChipKey) => ({ accountId, pageRequest: COUNT_PAGE, filter: inviteCountFilter(countChip) })
  const skipInviteCounts = { skip: !hasInviteScope }

  const { data: pendingCount } = useGetInvitesQuery(inviteCountArgs('pending'), skipInviteCounts)
  const { data: expiredCount } = useGetInvitesQuery(inviteCountArgs('expired'), skipInviteCounts)
  const { data: requestedCount, isError: requestedCountError } = useGetInvitesQuery(inviteCountArgs('requested'), skipInviteCounts)

  const { data: advisorRequested, isError: advisorError } = useGetInvitesQuery(
    { accountId, pageRequest: COUNT_PAGE, filter: requestedSeatTypeCountFilter(REQUESTED_SEAT_TYPES.advisor) },
    skipInviteCounts,
  )
  const { data: assistantRequested, isError: assistantError } = useGetInvitesQuery(
    { accountId, pageRequest: COUNT_PAGE, filter: requestedSeatTypeCountFilter(REQUESTED_SEAT_TYPES.assistant) },
    skipInviteCounts,
  )

  const rows = useMemo<TeamRow[]>(
    () => (showsInvites ? (invitePage?.data ?? []).map(toInviteRow) : (memberPage?.data ?? []).map(toMemberRow)),
    [showsInvites, invitePage, memberPage],
  )

  const refetch = useCallback(() => {
    if (!showsInvites) {
      refetchMembers()
    }
    refetchActiveCount()
  }, [showsInvites, refetchMembers, refetchActiveCount])

  return {
    rows,
    totalRows: (showsInvites ? invitePage?.page.totalElements : memberPage?.page.totalElements) ?? 0,
    isFetching: showsInvites ? invitesFetching : membersFetching,
    counts: {
      active: activeCount?.page.totalElements,
      pending: pendingCount?.page.totalElements,
      expired: expiredCount?.page.totalElements,
      requested: requestedCount?.page.totalElements,
    },
    requestedSeats: { advisor: advisorRequested?.page.totalElements, assistant: assistantRequested?.page.totalElements },
    requestedSeatsError: requestedCountError || advisorError || assistantError,
    refetch,
  }
}
